<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h4>Templates</h4>
      <div>
        <button class="badgebutton success active mr-2" @click="showCategoryModal(null)">
          <i class="far fa-folder-plus mr-1"></i>
          Add Category
        </button>
        <button class="badgebutton success active" @click="addTemplate">
          <i class="far fa-plus mr-1"></i>
          Add Template
        </button>
      </div>
    </div>

    <div class="bg-white p-3 shadow-sm mb-3 rounded-sm">
      <div class="row">
        <div class="col-4">
          <div class="input-group input-group-sm">
            <select class="form-control" v-model="templateType">
              <option v-for="option in templateTypeOptions" :value="option.id" :key="option.id">{{ option.name }}</option>
            </select>
          </div>
        </div>
        <div class="col-8">
          <div class="input-group input-group-sm mr-3 text-nowrap">
            <div class="input-group-prepend">
              <span class="input-group-text">
                <i class="far fa-search"></i>
              </span>
            </div>
            <input type="text" v-model="searchString" class="form-control" placeholder="Search..." aria-label="Search for templates" />
          </div>
        </div>
      </div>
    </div>

    <div style="height: calc(100vh - 280px); overflow-y: auto; overflow-x: hidden">
      <div v-if="loading" class="text-center">
        <loading-spinner></loading-spinner>
      </div>
      <div v-if="!loading">
        <!-- Categories with templates -->
        <div v-for="group in filteredCategories" :key="group.category ? group.category.guid : 'uncategorized'" class="mb-4">
          <!-- Category Header -->
          <div
            class="category-header d-flex justify-content-between align-items-center p-2 bg-light rounded-top cursor-pointer"
            @click="toggleCategory(group.category ? group.category.guid : 'uncategorized')"
          >
            <div class="d-flex align-items-center">
              <i
                class="far mr-2"
                :class="isCategoryExpanded(group.category ? group.category.guid : 'uncategorized') ? 'fa-chevron-down' : 'fa-chevron-right'"
              ></i>
              <i v-if="group.category && group.category.icon" :class="group.category.icon" class="mr-2"></i>
              <strong>{{ group.category ? group.category.name : 'Uncategorized' }}</strong>
            </div>
            <div v-if="group.category" @click.stop>
              <button
                class="btn btn-xs btn-info mr-2"
                v-if="canMoveUp(group.category)"
                @click="moveCategoryUp(group.category)"
              >
                <i class="far fa-arrow-alt-up"></i>
              </button>
              <button
                class="btn btn-xs btn-info mr-2"
                v-if="canMoveDown(group.category)"
                @click="moveCategoryDown(group.category)"
              >
                <i class="far fa-arrow-alt-down"></i>
              </button>
              <button class="btn btn-xs btn-info mr-2" @click="showCategoryModal(group.category)">
                <i class="far fa-pencil-alt"></i>
              </button>
              <button class="btn btn-xs btn-info" @click="confirmDeleteCategory(group.category)">
                <i class="far fa-trash"></i>
              </button>
            </div>
          </div>

          <!-- Templates in category -->
          <div v-show="isCategoryExpanded(group.category ? group.category.guid : 'uncategorized')" class="category-content">
            <div v-if="!group.templates.length" class="bg-white p-3 text-muted text-center">
              No templates in this category yet. Create one and assign this category to it.
            </div>
            <template-card
              v-for="(template, index) in group.templates"
              :key="template.template.id"
              :template="template"
              :is-last="index === group.templates.length - 1"
              @edit-category="showTemplateCategoryModal"
              @edit-schedule="editSchedule"
              @edit-availability="editAvailability"
              @remove="confirmRemoveTemplate"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Schedule Modal -->
    <b-modal
      ref="editTemplateModal"
      id="editTemplateModal"
      hide-footer
      hide-header
      body-class="modal-body-wo"
      size="lg"
      :no-close-on-backdrop="false"
      @hidden="loadData"
    >
      <board-item-schedule-edit :current-template-id="currentTemplateId"></board-item-schedule-edit>
    </b-modal>

    <!-- Availability Modal -->
    <b-modal
      ref="editAvailabilityModal"
      id="editAvailabilityModal"
      hide-footer
      hide-header
      body-class="modal-body-wo"
      size="lg"
      :no-close-on-backdrop="true"
      @hidden="loadData"
    >
      <board-item-template-availability-edit :current-template-id="currentTemplateId"></board-item-template-availability-edit>
    </b-modal>

    <!-- Category Edit Modal -->
    <b-modal
      ref="categoryEditModal"
      id="categoryEditModal"
      hide-footer
      hide-header
      body-class="modal-body-wo"
      size="lg"
      :no-close-on-backdrop="true"
      @hidden="loadData"
    >
      <template-category-edit-modal
        :category="editingCategory"
        :board-guid="boardId"
        :default-sort-order="allCategories.length"
      />
    </b-modal>

    <!-- Template Category Select Modal -->
    <b-modal
      ref="templateCategoryModal"
      id="templateCategoryModal"
      hide-footer
      hide-header
      body-class="modal-body-wo"
      size="lg"
      :no-close-on-backdrop="true"
      @hidden="loadData"
    >
      <template-category-select-modal
        :template="currentTemplate"
        :categories="allCategories"
      />
    </b-modal>

    <router-view></router-view>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { boardItemTemplateClient, templateCategoryClient } from '@/apiclient/opsboard-api';
import {
  SpecialOccurrenceType,
  TemplateCategoryWithTemplatesDto,
  TemplateCategoryDto,
  BoardItemDto,
} from '@/apiclient/apiclient_generated';
import BoardItemScheduleEdit from '@/components/BoardSettings/BoardItemScheduleEdit.vue';
import TaskCard from '@/components/TaskCard.vue';
import { BoardModule, BoardStore } from '@/store/board';
import RouteNames from '@/types/routeNames';
import BoardItemTemplateAvailabilityEdit from '@/components/BoardSettings/BoardItemTemplateAvailabilityEdit.vue';
import { orderBy } from 'lodash';
import LoadingSpinner from '@/components/Misc/LoadingSpinner.vue';
import { ConfirmModal } from '@/helpers/confirm-modal';
import { GlobalHelper } from '@/helpers/global-helper';
import { LabelModule, LabelStore } from '@/store/label';
import TemplateCategoryEditModal from '@/components/BoardSettings/TemplateCategoryEditModal.vue';
import TemplateCategorySelectModal from '@/components/BoardSettings/TemplateCategorySelectModal.vue';
import TemplateCard from '@/components/BoardSettings/TemplateCard.vue';

Component.registerHooks(['beforeRouteUpdate', 'beforeRouteEnter']);

@Component({
  components: {
    LoadingSpinner,
    BoardItemTemplateAvailabilityEdit,
    TaskCard,
    BoardItemScheduleEdit,
    TemplateCategoryEditModal,
    TemplateCategorySelectModal,
    TemplateCard,
  },
})
export default class BoardTemplates extends Vue {
  private boardId: string;
  private boardStore: BoardModule = BoardStore;
  private labelStore: LabelModule = LabelStore;

  private categoryGroups: TemplateCategoryWithTemplatesDto[] = [];
  private expandedCategories: Set<string> = new Set(['uncategorized']);

  private currentTemplateId = '';
  private searchString = '';
  private templateType = 'All';
  private loading = true;

  // Category editing
  private editingCategory: TemplateCategoryDto | null = null;

  // Template category selection
  private currentTemplate: BoardItemDto | null = null;

  created() {
    this.boardId = this.$route.params.id;
  }

  beforeRouteUpdate(to: any, from: any, next: any) {
    if (to.name == RouteNames.biTemplates) {
      this.loadData();
      next();
    } else {
      next();
    }
  }

  get templateTypeOptions(): any[] {
    return [
      { id: 'All', name: 'All Templates' },
      { id: SpecialOccurrenceType.None, name: 'Board Templates' },
      { id: SpecialOccurrenceType.TechnicalOccurrence, name: 'Technial Occurrence Templates' },
      { id: SpecialOccurrenceType.Comat, name: 'COMAT Templates' },
      { id: SpecialOccurrenceType.Diversion, name: 'Diversion Templates' },
      { id: 'Scheduled', name: 'Templates with Schedules' },
    ];
  }

  get hasAnyTemplates(): boolean {
    return this.categoryGroups.some((g) => g.templates && g.templates.length > 0);
  }

  get allCategories(): TemplateCategoryDto[] {
    return this.categoryGroups.filter((g) => g.category).map((g) => g.category!);
  }

  async loadData() {
    await this.boardStore.getBoard(this.boardId);
    await this.labelStore.loadSelectedBoardLabels(this.boardId);
    this.categoryGroups = await boardItemTemplateClient.getTemplates(this.boardId);

    // Expand all categories by default
    this.categoryGroups.forEach((g) => {
      const key = g.category ? g.category.guid! : 'uncategorized';
      this.expandedCategories.add(key);
    });

    this.loading = false;
  }

  async mounted() {
    await this.loadData();
  }

  get filteredCategories(): TemplateCategoryWithTemplatesDto[] {
    if (!this.searchString && this.templateType === 'All') {
      return this.categoryGroups;
    }

    return this.categoryGroups
      .map((group) => {
        let templates = group.templates || [];

        if (this.searchString) {
          templates = templates.filter(
            (t) =>
              t.template!.title!.toLowerCase().indexOf(this.searchString.toLowerCase()) !== -1 ||
              (t.template!.description ? t.template!.description.toLowerCase().indexOf(this.searchString.toLowerCase()) !== -1 : false)
          );
        }

        if (this.templateType !== 'All') {
          if (this.templateType === 'Scheduled') {
            templates = templates.filter((_) => _.schedules!.length !== 0);
          } else {
            templates = templates.filter(
              (_) => _.availability!.filter((av) => av.specialOccurrenceType === this.templateType).length !== 0
            );
            templates.forEach((res) => {
              //@ts-ignore
              res.sortOrder = res.availability!.find((_) => _.specialOccurrenceType === this.templateType)?.sortOrder ?? 0;
            });
            templates = orderBy(templates, 'sortOrder');
          }
        }

        return {
          category: group.category,
          templates: templates,
        } as TemplateCategoryWithTemplatesDto;
      })
      .filter((group) => group.templates!.length > 0 || (!this.searchString && this.templateType === 'All'));
  }

  toggleCategory(categoryKey: string) {
    if (this.expandedCategories.has(categoryKey)) {
      this.expandedCategories.delete(categoryKey);
    } else {
      this.expandedCategories.add(categoryKey);
    }
    this.expandedCategories = new Set(this.expandedCategories);
  }

  isCategoryExpanded(categoryKey: string): boolean {
    return this.expandedCategories.has(categoryKey);
  }

  addTemplate() {
    this.$router.push({ name: 'TemplateDetail', params: { itemid: 'new' } });
  }

  showCategoryModal(category: TemplateCategoryDto | null) {
    this.editingCategory = category;
    this.$bvModal.show('categoryEditModal');
  }

  confirmDeleteCategory(category: TemplateCategoryDto) {
    ConfirmModal.confirm(
      this,
      `Are you sure you want to delete the category "${category.name}"? Templates will become uncategorized.`,
      'Confirm delete',
      'Yes, delete category',
      'No',
      () => this.deleteCategory(category.guid!),
      null,
      'danger'
    );
  }

  async deleteCategory(guid: string) {
    try {
      await templateCategoryClient.delete(guid);
      GlobalHelper.NotifySuccess('Category deleted');
      await this.loadData();
    } catch (error) {
      GlobalHelper.NotifyError('Failed to delete category');
    }
  }

  canMoveUp(category: TemplateCategoryDto): boolean {
    const index = this.allCategories.findIndex((c) => c.guid === category.guid);
    return index > 0;
  }

  canMoveDown(category: TemplateCategoryDto): boolean {
    const index = this.allCategories.findIndex((c) => c.guid === category.guid);
    return index >= 0 && index < this.allCategories.length - 1;
  }

  async moveCategoryUp(category: TemplateCategoryDto) {
    const categories = this.allCategories;
    const index = categories.findIndex((c) => c.guid === category.guid);
    if (index <= 0) return;

    const prevCategory = categories[index - 1];
    await this.swapCategorySortOrder(category, prevCategory);
  }

  async moveCategoryDown(category: TemplateCategoryDto) {
    const categories = this.allCategories;
    const index = categories.findIndex((c) => c.guid === category.guid);
    if (index < 0 || index >= categories.length - 1) return;

    const nextCategory = categories[index + 1];
    await this.swapCategorySortOrder(category, nextCategory);
  }

  async swapCategorySortOrder(cat1: TemplateCategoryDto, cat2: TemplateCategoryDto) {
    try {
      const tempSortOrder = cat1.sortOrder;
      cat1.sortOrder = cat2.sortOrder;
      cat2.sortOrder = tempSortOrder;

      await Promise.all([
        templateCategoryClient.update(cat1.guid!, cat1),
        templateCategoryClient.update(cat2.guid!, cat2),
      ]);

      await this.loadData();
    } catch (error) {
      GlobalHelper.NotifyError('Failed to update category order');
    }
  }

  showTemplateCategoryModal(template: BoardItemDto) {
    this.currentTemplate = template;
    this.$bvModal.show('templateCategoryModal');
  }

  editSchedule(id: string) {
    this.currentTemplateId = id;
    this.$bvModal.show('editTemplateModal');
  }

  editAvailability(id: string) {
    this.currentTemplateId = id;
    this.$bvModal.show('editAvailabilityModal');
  }

  confirmRemoveTemplate(id: string) {
    ConfirmModal.confirm(
      this,
      `Are you sure you want to remove this template?`,
      'Confirm delete',
      'Yes, remove template',
      'No',
      () => this.removeTemplate(id),
      null,
      'danger'
    );
  }

  async removeTemplate(id: string) {
    try {
      await boardItemTemplateClient.removeTemplate(id);
      await this.loadData();
      GlobalHelper.NotifySuccess('Template removed');
    } catch (error) {
      GlobalHelper.NotifyError('Template could not be removed');
    }
  }
}
</script>

<style scoped lang="scss">
.category-header {
  border: 1px solid #dee2e6;
  cursor: pointer;

  &:hover {
    background-color: #e9ecef !important;
  }
}

.category-content {
  border-left: 1px solid #dee2e6;
  border-right: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
  background-color: #fff;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
